import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Box, Container, Button, Typography, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, NewPageTitleBar } from '@/components';
import ShopSelector from '@/components/selectors/ShopSelector';

import { getMenuData } from '@core/data/menuData';
import { useApiStore, useSettingsStore } from '@core/store';
import { apiClient } from '@core/api';
import { notificationService } from '@core/notifications/NotificationService';
import { FCSecurePage, useRegisterRefresh, useShopPersistence } from '@/components/shared/navigation/securePage.ts';
import { ShopAutomation } from '@core/types';
import { ItemStatus } from '@features/shopautomation/types.ts';
import { DateTime } from 'luxon';
import { useMobileHeaderPanelContent } from '@layouts/MobileHeaderPanel.tsx';
import HealthStatusMonitoringTable from '@features/shopautomation/components/Shops/HealthStatusMonitoringTable.tsx';

export interface OperationModeState {
    currentOperationMode: string;
    operationModeScheduleActive: boolean;
}

export interface ShopStatusResponse {
    id: string;
    name: string;
    healthStatusType: string;
    items: ItemStatus[];
    operationModeState?: OperationModeState;
}

const ShopAutomationShops: FCSecurePage = () => {
    const { t } = useTranslation();
    const { loadShopIds, loadOperationModes } = useApiStore();
    const { isMobile } = useSettingsStore();

    const [loading, setLoading] = useState(false);
    const [shopAutomations, setShopAutomations] = useState<ShopAutomation[]>([]);
    const [shopStatusData, setShopStatusData] = useState<ShopStatusResponse[]>([]);
    const { selectedShopIds, setSelectedShopIds } = useShopPersistence();
    const [lastUpdate, setLastUpdate] = useState<DateTime | undefined>(undefined);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const load = useCallback(
        async (silent = false) => {
            if (!selectedShopIds[0]) return;
            try {
                const response = await apiClient.get<ShopAutomation[]>(`shopAutomation/${selectedShopIds[0]}`);
                setShopAutomations(response.data || []);
            } catch (error: any) {
                if (!silent) {
                    notificationService.error(t('error'), t('errors.loadingData'));
                    setShopAutomations([]);
                    console.error('Failed to load shop automation data:', error);
                }
            }
        },
        [selectedShopIds[0], t]
    );

    const reload = async () => {
        setLoading(true);
        try {
            await Promise.all([load(), loadShopStatus()]);
        } catch (err: any) {
            console.error('Reload failed:', err);
        } finally {
            setLoading(false);
        }
    };

    useRegisterRefresh(reload, loading || !selectedShopIds[0]);

    useEffect(() => {
        const initializeData = async () => {
            setLoading(true);
            try {
                await Promise.all([loadShopIds(), loadOperationModes(), loadShopStatus()]);
            } catch (error: any) {
                console.error('Initialization failed:', error);
            } finally {
                setLoading(false);
            }
        };
        initializeData();
    }, [loadShopIds, loadOperationModes]);

    const loadShopStatus = useCallback(async () => {
        try {
            const response = await apiClient.get<ShopStatusResponse[]>('shop/status');
            setShopStatusData(response.data || []);
            setLastUpdate(DateTime.now());
        } catch (error: any) {
            notificationService.error(t('error'), t('errors.loadingData'));
            setShopStatusData([]);
            console.error('Failed to load shop status data:', error);
        }
    }, [selectedShopIds[0], t]);

    useEffect(() => {
        if (selectedShopIds[0]) {
            reload();
        }
    }, [selectedShopIds[0]]);

    useEffect(() => {
        if (!selectedShopIds[0]) return;
        const intervalId = setInterval(() => {
            load(true);
        }, 30000);
        return () => clearInterval(intervalId);
    }, [selectedShopIds[0], load]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, []);

    const filterContent = useMemo(
        () => (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <ShopSelector
                    value={selectedShopIds[0] ?? null}
                    onChange={(id) => setSelectedShopIds(id ? [id] : [])}
                    label={t('shop')}
                />
            </Box>
        ),
        [selectedShopIds, t]
    );

    useMobileHeaderPanelContent(filterContent);

    const shopItems = shopStatusData.find((s) => s.id === selectedShopIds[0])?.items ?? [];

    return (
        <Box sx={{ width: '100%', bgcolor: 'background.default' }}>
            <NewPageTitleBar
                icon={<i className="bi bi-toggles" style={{ fontSize: '16px' }} />}
                title={t('remoteControl')}
                menus={getMenuData()}
                currentRoute={{ path: '/manage-shop/remote-control' }}
                t={t}
                lastUpdate={lastUpdate}
                hasAutoRefresh={false}
            />

            <Container maxWidth={false} sx={{ py: 1 }}>
                {!isMobile && (
                    <Box sx={{ mb: 5 }}>
                        <ShopSelector
                            value={selectedShopIds[0] ?? null}
                            onChange={(id) => setSelectedShopIds(id ? [id] : [])}
                            label={t('shop')}
                            disabled={loading}
                        />
                    </Box>
                )}

                {loading ? (
                    <LoadingSpinner showBackground={false} />
                ) : (
                    <Box>
                        <HealthStatusMonitoringTable shopAutomation={shopAutomations[0]} itemStatus={shopItems} />
                    </Box>
                )}
            </Container>
        </Box>
    );
};

export default ShopAutomationShops;
